import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Save } from 'lucide-react';
import api from '../api/client';
import PinFieldManager from './PinFieldManager';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Phoenix', label: 'Arizona' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
];

const HOURS = [6, 7, 8, 9, 10].map(h => ({ value: h, label: `${h}:00 AM` }));

function getInitials(f, l) {
  return `${(f || '?')[0]}${(l || '')[0] || ''}`.toUpperCase();
}

function rateColor(rate) {
  if (rate === null) return '#6b6350';
  if (rate >= 85) return '#16a34a';
  if (rate >= 70) return '#c9a84c';
  return '#dc2626';
}

function StatusBadge({ status }) {
  const map = {
    embedded: { color: '#16a34a', label: 'In Knowledge Base' },
    approved: { color: '#16a34a', label: 'Approved' },
    pending: { color: '#c9a84c', label: 'Pending Review' },
    rejected: { color: '#6b6350', label: 'Rejected' },
  };
  const s = map[status] || map.pending;
  return <span style={{ fontSize: 10, color: s.color, fontWeight: 600 }}>{s.label}</span>;
}

export default function ProfilePanel({ open, onClose }) {
  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/profile/me').then(({ data }) => {
      setProfile(data.profile);
      setForm({
        first_name: data.profile?.first_name || '',
        last_name: data.profile?.last_name || '',
        phone: data.profile?.phone || '',
        job_title: data.profile?.job_title || '',
        bio: data.profile?.bio || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open || tab !== 'stats') return;
    api.get('/profile/ai-stats').then(({ data }) => setStats(data)).catch(() => {});
  }, [open, tab]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/profile/me', form);
      setProfile(p => ({ ...p, ...data.profile }));
    } catch (e) { /* */ }
    finally { setSaving(false); }
  };

  const handleSettingChange = async (key, value) => {
    try {
      const { data } = await api.patch('/profile/me', { [key]: value });
      setProfile(p => ({ ...p, ...data.profile }));
    } catch (e) { /* */ }
  };

  const handleNotifChange = async (key, value) => {
    try {
      const { data } = await api.patch('/profile/me', { notification_prefs: { [key]: value } });
      setProfile(p => ({ ...p, ...data.profile }));
    } catch (e) { /* */ }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const { data } = await api.post('/profile/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile(p => ({ ...p, avatar_url: data.avatar_url }));
    } catch (e) { /* */ }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'stats', label: 'My AI Stats' },
    { id: 'pins', label: 'My Pins' },
    { id: 'field_pins', label: 'Field Pins' },
  ];

  // Field Pins tab state — record-type picker and selection
  const [fieldPinsRecordTypes, setFieldPinsRecordTypes] = useState([]);
  const [fieldPinsSelectedRt, setFieldPinsSelectedRt] = useState('');
  useEffect(() => {
    if (!open || tab !== 'field_pins') return;
    if (fieldPinsRecordTypes.length > 0) return;
    api.get('/chat-ai/record-types').then(({ data }) => {
      const rts = data.record_types || [];
      setFieldPinsRecordTypes(rts);
      if (rts.length > 0 && !fieldPinsSelectedRt) setFieldPinsSelectedRt(rts[0].id);
    }).catch(() => {});
  }, [open, tab]);

  const notifPrefs = profile?.notification_prefs || {};

  return (
    <>
      {/* Backdrop */}
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} />}

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, zIndex: 50,
        background: '#0e0f11', borderLeft: '1px solid #2a2a35',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a35', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ color: '#f5f0e8', fontSize: 14, fontWeight: 600 }}>My Profile</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#8a8070', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '8px 16px', borderBottom: '1px solid #2a2a35', flexShrink: 0 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer',
                border: 'none', transition: 'all 0.2s',
                background: tab === t.id ? '#1a1710' : 'transparent',
                color: tab === t.id ? '#c9a84c' : '#6b6350',
              }}>{t.label}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {loading && <p style={{ color: '#6b6350', fontSize: 12, textAlign: 'center', padding: 32 }}>Loading...</p>}

          {/* ─── Profile Tab ─── */}
          {!loading && tab === 'profile' && profile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Avatar */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div onClick={() => fileRef.current?.click()} style={{
                  width: 72, height: 72, borderRadius: '50%', background: '#1a1710',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', border: '2px solid #3d3418', position: 'relative', overflow: 'hidden',
                }}>
                  {profile.avatar_url ? (
                    <img src={`/api/files/avatar/${profile.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <span style={{ color: '#c9a84c', fontSize: 24, fontWeight: 700 }}>{getInitials(profile.first_name, profile.last_name)}</span>
                  )}
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', padding: '3px 0', textAlign: 'center' }}>
                    <Camera size={12} style={{ color: '#c9a84c' }} />
                  </div>
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
                {uploading && <span style={{ color: '#c9a84c', fontSize: 10 }}>Uploading...</span>}
              </div>

              {/* Personal Info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <InputField label="First Name" value={form.first_name} onChange={v => setForm(f => ({ ...f, first_name: v }))} />
                  <InputField label="Last Name" value={form.last_name} onChange={v => setForm(f => ({ ...f, last_name: v }))} />
                </div>
                <InputField label="Job Title" value={form.job_title} onChange={v => setForm(f => ({ ...f, job_title: v }))} placeholder="e.g. Paralegal" />
                <InputField label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} placeholder="310-555-1234" />
                <div>
                  <label style={labelStyle}>Bio</label>
                  <textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value.slice(0, 200) }))}
                    placeholder="Short bio..." rows={2} maxLength={200} style={{ ...inputStyle, resize: 'none', minHeight: 50 }} />
                  <span style={{ fontSize: 9, color: '#5a4a30' }}>{(form.bio || '').length}/200</span>
                </div>
                <button onClick={handleSave} disabled={saving}
                  style={{ padding: '8px 0', borderRadius: 12, border: '1px solid #d4b060', cursor: 'pointer', fontWeight: 600, fontSize: 12, background: 'linear-gradient(135deg, #c9a84c, #a67c32)', color: '#fff8e6', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Briefing Settings */}
              <Section title="Morning Briefing">
                <ToggleRow label="Morning briefing email" checked={profile.briefing_enabled}
                  onChange={v => handleSettingChange('briefing_enabled', v)} />
                {profile.briefing_enabled && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Time</label>
                      <select value={profile.briefing_hour || 8} onChange={e => handleSettingChange('briefing_hour', parseInt(e.target.value))}
                        style={inputStyle}>
                        {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Timezone</label>
                      <select value={profile.timezone || 'America/New_York'} onChange={e => handleSettingChange('timezone', e.target.value)}
                        style={inputStyle}>
                        {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </Section>

              {/* Notification Prefs */}
              <Section title="Notifications">
                <ToggleRow label="Email when task assigned" checked={notifPrefs.email_task_assigned !== false}
                  onChange={v => handleNotifChange('email_task_assigned', v)} />
                <ToggleRow label="Email when task overdue" checked={notifPrefs.email_task_overdue !== false}
                  onChange={v => handleNotifChange('email_task_overdue', v)} />
                <ToggleRow label="Email when @mentioned" checked={notifPrefs.email_mentions !== false}
                  onChange={v => handleNotifChange('email_mentions', v)} />
              </Section>

              {/* Read-only */}
              <Section title="Account">
                <InfoRow label="Email" value={profile.email} />
                <InfoRow label="Role" value={profile.role_name || 'None'} />
                <InfoRow label="Team" value={profile.team_name || 'None'} />
                <InfoRow label="Member since" value={new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} />
              </Section>
            </div>
          )}

          {/* ─── Stats Tab ─── */}
          {!loading && tab === 'stats' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {stats ? (
                <>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <StatCard label="Confirm Rate" value={stats.confirm_rate !== null ? `${stats.confirm_rate}%` : '—'} color={rateColor(stats.confirm_rate)} />
                    <StatCard label="Queries (30d)" value={stats.total_queries} color="#c9a84c" />
                  </div>
                  {stats.nudge && (
                    <div style={{ background: '#1a1710', border: '1px solid #3d3418', borderRadius: 12, padding: '10px 14px', fontSize: 12, color: '#c9a84c' }}>
                      {stats.nudge}
                    </div>
                  )}
                  {stats.most_corrected?.length > 0 && (
                    <Section title="Most Overridden">
                      {stats.most_corrected.map((c, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                          <span style={{ color: '#f5f0e8', fontSize: 12 }}>{c.intent}</span>
                          <span style={{ color: '#8a6540', fontSize: 12 }}>{c.count}x</span>
                        </div>
                      ))}
                    </Section>
                  )}
                  {stats.total_queries < 5 && (
                    <p style={{ color: '#6b6350', fontSize: 12, textAlign: 'center', padding: 20 }}>Use the AI chat more to see detailed stats here.</p>
                  )}
                </>
              ) : (
                <p style={{ color: '#6b6350', fontSize: 12, textAlign: 'center', padding: 32 }}>Loading stats...</p>
              )}
            </div>
          )}

          {/* ─── Pins Tab ─── */}
          {!loading && tab === 'pins' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stats?.pins?.length > 0 ? (
                stats.pins.map(p => (
                  <div key={p.id} style={{ background: '#1a1b1f', border: '1px solid #2a2a35', borderRadius: 10, padding: '10px 12px' }}>
                    <p style={{ color: '#f5f0e8', fontSize: 12, margin: '0 0 4px', lineHeight: 1.4 }}>{p.preview}...</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <StatusBadge status={p.status} />
                      <span style={{ fontSize: 9, color: '#5a4a30' }}>{new Date(p.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <p style={{ color: '#6b6350', fontSize: 12 }}>You haven't pinned any answers yet.</p>
                  <p style={{ color: '#5a4a30', fontSize: 11, marginTop: 4 }}>Look for the pin icon on AI responses.</p>
                </div>
              )}
            </div>
          )}

          {/* ─── Field Pins Tab ─── */}
          {!loading && tab === 'field_pins' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={labelStyle}>Record Type</label>
                <select
                  value={fieldPinsSelectedRt}
                  onChange={(e) => setFieldPinsSelectedRt(e.target.value)}
                  style={inputStyle}
                >
                  {fieldPinsRecordTypes.length === 0 && <option value="">Loading...</option>}
                  {fieldPinsRecordTypes.map((rt) => (
                    <option key={rt.id} value={rt.id}>{rt.name}</option>
                  ))}
                </select>
                <p style={{ fontSize: 10, color: '#6b6350', marginTop: 6 }}>
                  Manage which fields appear in the left panel's Pinned Fields section for this record type.
                </p>
              </div>
              {fieldPinsSelectedRt && (
                <div style={{ marginTop: 4 }}>
                  <PinFieldManager
                    key={fieldPinsSelectedRt}
                    recordTypeId={fieldPinsSelectedRt}
                    currentPins={[]}
                    onClose={() => {}}
                    onSaved={() => {}}
                    embedded={true}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Shared sub-components ──────────────────────────────
const labelStyle = { display: 'block', fontSize: 10, color: '#8a6540', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' };
const inputStyle = { width: '100%', background: '#1a1b1f', border: '1px solid #2a2a35', color: '#f5f0e8', fontSize: 12, borderRadius: 8, padding: '6px 10px', outline: 'none' };

function InputField({ label, value, onChange, placeholder }) {
  return (
    <div style={{ flex: 1 }}>
      <label style={labelStyle}>{label}</label>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={inputStyle} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p style={{ fontSize: 11, color: '#c9a84c', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</p>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
      <span style={{ color: '#f5f0e8', fontSize: 12 }}>{label}</span>
      <button onClick={() => onChange(!checked)} style={{
        width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', transition: 'background 0.2s',
        background: checked ? '#c9a84c' : '#2a2a35', position: 'relative',
      }}>
        <div style={{
          width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
          left: checked ? 18 : 2, transition: 'left 0.2s',
        }} />
      </button>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: '#8a6540', fontSize: 11 }}>{label}</span>
      <span style={{ color: '#f5f0e8', fontSize: 11 }}>{value}</span>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ flex: 1, background: '#1a1b1f', border: '1px solid #2a2a35', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: '#8a6540', marginTop: 2 }}>{label}</div>
    </div>
  );
}
